'use strict';

import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {UrlService} from '../../modules/common/url.service';
import {Constants} from '../../modules/constants';
import {ITranslationRequest} from '../../modules/translation/translate.service';
import {ErrorUtil} from '../../modules/util/error-util';
import {IPreloadResult, TranslationPreloadService} from '../../modules/translation/translation-preload.service';
import {LoggerService} from '../../modules/common/logger.service';
import {UsanaObject} from '../../modules/usana-object';

/**
 * Handles translations for the dotcom area and mirrors the caching and deduplication patterns
 * established by TranslateService. TranslateService was intentionally left unchanged due to the
 * risk of regressions across the broader cart-ux application.
 */
@Injectable({
    providedIn: 'root'
})
export class DotcomTranslateService extends UsanaObject {

    private readonly promiseMap: {[key: string]: Promise<string>} = {};
    private readonly stringMap: {[key: string]: string} = {};

    constructor(
        loggerService: LoggerService,
        private http: HttpClient,
        private urlService: UrlService,
        private translationPreloadService: TranslationPreloadService
    ) {
        super(loggerService);
    }

    translate(tag: string, teasLocale: string): Promise<string> {
        const cacheKey = `${teasLocale}:${tag}`;

        if (this.stringMap[cacheKey]) {
            return Promise.resolve(this.stringMap[cacheKey]);
        }
        if (this.promiseMap[cacheKey]) {
            return this.promiseMap[cacheKey];
        }

        this.promiseMap[cacheKey] = this.translationPreloadService.preloadPromise
            .then((result: IPreloadResult) => {
                if (this.stringMap[cacheKey]) {
                    return this.stringMap[cacheKey];
                }
                const preloaded = result.dotcomTranslations[tag];
                if (preloaded) {
                    this.stringMap[cacheKey] = preloaded;
                    return preloaded;
                }

                const params = new HttpParams().set('tag', encodeURIComponent(tag)).set('i18nPackage', 'usanadotcom');
                const headers = new HttpHeaders({
                    [Constants.LOCALE_OVERRIDE_HEADER]: teasLocale,
                    [Constants.TEAS_OVERRIDE_HEADER]: 'true'
                });
                const options = {
                    params,
                    responseType: 'text' as 'json',
                    headers
                };

                const url = this.urlService.buildCartUrl('/translation');
                this.log.debug('GET', `${url}?${params.toString()}`, {
                    headers: headers.keys().reduce((acc, key) => ({...acc, [key]: headers.get(key)}), {})
                });

                return this.http.get<string>(url, options)
                    .toPromise()
                    .then(
                        (val: string) => { this.stringMap[cacheKey] = val; return val; },
                        (err: any) => ErrorUtil.handleError(err)
                    );
            })
            .then(
                (val: string) => { delete this.promiseMap[cacheKey]; return val; },
                (err: any) => { delete this.promiseMap[cacheKey]; return ErrorUtil.handleError(err); }
            );

        return this.promiseMap[cacheKey];
    }

    translateRequest(request: ITranslationRequest, teasLocale: string): Promise<string> {
        return this.translate(request.tag, teasLocale)
            .then((translation: string) => this.interpolate(translation, request));
    }

    interpolate(translation: string, request: ITranslationRequest): string {
        if (!request.replacements?.size) {
            return translation;
        }
        let result = translation;
        request.replacements.forEach((value, key) => {
            result = result.split(`{${key}}`).join(value);
        });
        return result;
    }
}
